import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { Typography } from '@curology/ui-components-web/react/Typography';
import Image from 'next/image';
import React from 'react';

import { type Card } from '@/types/productSelectionAndCheckout/types';

import { ProductOptionsSelector } from '../../../productOptionsSelector';
import { IngredientsAccordion } from '../IngredientsAccordion';
import { ModalImage } from '../ModalImage';
import { ModalPricingVolumeAndProductOptionSelector } from '../ModalPricingVolumeAndProductOptionSelector';
import { ModalRatingAndNumReviews } from '../ModalRatingAndNumReviews';

export const SilkScreenModalContent = ({ modalCard }: { modalCard: Card }) => {
  const { modal } = modalCard;

  return (
    <>
      <ModalImage modal={modal} />
      <Typography className="cur-mt-4" size="h2">
        {modalCard.modal.title}
      </Typography>
      <div className="cur-mt-4">
        <ProductOptionsSelector
          currentFullSizeItemId={modalCard.fullSizeItemId}
          selectableOptions={modalCard.selectableOptions}
        />
      </div>
      <Typography className="cur-mt-4">{modal.tagline}</Typography>
      <ModalPricingVolumeAndProductOptionSelector modalCard={modalCard} />
      {modalCard.recommendationText && (
        <Highlight className="cur-mt-4" state="success">
          <div
            dangerouslySetInnerHTML={{
              __html: modalCard.recommendationText.modalCallout,
            }}
          ></div>
        </Highlight>
      )}
      <ModalRatingAndNumReviews modalCard={modalCard} />
      <Typography className="cur-mt-4">
        SilkScreen takes sun protection from daily chore to daily luxury. The
        weightless, non-nano SPF 50 mineral sunscreen serum melts into skin and
        enhances your natural glow.
      </Typography>
      <Typography className="cur-mt-4">
        It provides UVA and UVB protection with hydrating, antioxidant-rich
        ingredients—including hyaluronic acid—for a formula that is equal parts
        suncare and skincare. Designed for all skin types, this sunscreen evens
        skin tone, blurs pores and the look of fine lines, and helps prevent
        visible signs of aging.
      </Typography>
      <Typography className="cur-mt-4">
        Formulated with zinc oxide and titanium dioxide, this all-mineral
        sunscreen comes in two sheer tints: 1 (light-to-medium) and 2
        (medium-to-deep). The pigments are designed to cover the full range of
        skin tones—without white cast or pilling.
      </Typography>
      <div className="cur-mt-4">
        <Image
          alt="SilkScreen tints"
          height={564}
          src="https://images.ctfassets.net/8pzh4g8cs30t/6GS1attCpdmEAuzbMRDUzS/5b74f678b6850d7707e2b4bc78949da3/silkscreen-tints.jpg"
          style={{
            maxWidth: '100%',
          }}
          width={670}
        />
      </div>
      <Typography className="cur-mt-4" size="h3">
        How to use it
      </Typography>
      <Typography className="cur-mt-4">
        SilkScreen is for everyday use. It can be worn alone, seamlessly
        imparting a natural, dewy glow. The quick-absorbing serum also perfectly
        layers underneath makeup as a priming base.
      </Typography>
      <Typography className="cur-mt-4">
        Apply generously to face and neck:
      </Typography>
      <ul className="cur-list-disc cur-ml-4">
        <li className="cur-mt-4">
          <Typography>15 minutes before sun exposure</Typography>
        </li>
        <li className="cur-mt-4">
          <Typography>At least every 2 hours during exposure</Typography>
        </li>
        <li className="cur-mt-4">
          <Typography>Every 80 minutes if sweating or swimming</Typography>
        </li>
      </ul>
      <Typography className="cur-mt-4">
        UV rays are sneaky so always take additional precautions (like a hat and
        shades).
      </Typography>
      <Typography className="cur-mt-4" size="h3">
        Featured ingredients
      </Typography>
      <IngredientsAccordion
        body="Protects against both UVA and UVB rays"
        title="Zinc oxide 13.35%"
      />
      <IngredientsAccordion
        body="Protects against both UVA and UVB rays"
        title="Titanium dioxide 2.37%"
      />
      <IngredientsAccordion
        body="Attracts and retains moisture for visibly plumper skin"
        title="Hyaluronic acid"
      />
      <IngredientsAccordion
        body="Delivers antioxidant and anti-inflammatory effects to help fade dark spots"
        title="Licorice root extract"
      />
      <IngredientsAccordion
        body="Fights free radicals, soothes inflammation and visibly fades dark spots"
        title="Niacinamide"
      />
      <Typography className="cur-mt-4" size="h3">
        Full ingredients list
      </Typography>
      <Typography className="cur-mt-4">
        Active ingredients: Zinc oxide 13.35%, Titanium dioxide 2.37%
      </Typography>
      <Typography className="cur-mt-4">
        Caprylic/Capric Triglyceride, Water, Diisopropyl Sebacate,
        Polymethylsilsesquioxane, Undecane, Aloe Barbadensis Leaf Juice,
        Glycerin, Polyglyceryl-6 Polyricinoleate, Isododecane, Dimethicone,
        Mica, Polysilicone-11, Tridecane, Argania Spinosa Kernel Oil,
        Polyhydroxystearic Acid, Tocopheryl Acetate, Polyglyceryl-2 Isostearate,
        Disteardimonium Hectorite, Phenoxyethanol, Dimethicone Crosspolymer,
        Sodium Chloride, Triethoxycaprylylsilane, Alumina, Propanediol, Iron
        Oxide (CI 77492), Magnesium Stearate, Jojoba Esters, Disodium EDTA,
        1,2-Hexanediol, Bisabolol, Propylene Carbonate, Titanium Dioxide (CI
        77891), Niacinamide, Iron Oxides (CI 77491), Laureth-12,
        Caprylhydroxamic Acid, Iron Oxides (Black, CI 77499), Sodium
        Hyaluronate, Ethylhexylglycerin, Glycyrrhiza Glabra (Licorice) Root
        Extract, Iron Oxides (CI77499)
      </Typography>
    </>
  );
};
